// this whole page is only rendered on the checks route
/* eslint-disable local/no-direct-route-config-access */
import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackL1PageView } from 'src/lib/analytics/amplitude/hooks/useTrackL1PageView';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { useGraphRef } from '../hooks/useGraphRef';
import { recentChecksRouteConfig } from '../routes';

import { ChecksFilters } from './checksFilters/ChecksFilters';
import { CheckWorkflowsListPage } from './checkWorkflowsListPage/CheckWorkflowsListPage';

export const ChecksPage = () => {
  const graphRef = useGraphRef();
  useTrackL1PageView('checksL1', { graphRef });

  if (!graphRef) {
    throw new Error('Expected to have a graph ref');
  }

  const { view, author, branch, subgraph, status, variants } = useRouteParams(
    recentChecksRouteConfig,
  );
  const selectedAuthors = typeof author === 'string' ? [author] : author;
  const selectedSubgraphs =
    typeof subgraph === 'string' ? [subgraph] : subgraph;
  const selectedBranches = typeof branch === 'string' ? [branch] : branch;
  const isVariantTab = view === 'variant';
  const selectedVariants = isVariantTab
    ? [graphRef.graphVariant]
    : typeof variants === 'string'
      ? [variants]
      : variants;

  const { data } = useQuery<
    GraphQLTypes.ServiceHasWorkflowsQuery,
    GraphQLTypes.ServiceHasWorkflowsQueryVariables
  >(
    gql`
      query ServiceHasWorkflowsQuery(
        $graphId: ID!
        $checkWorkflowsFilter: CheckFilterInput!
      ) {
        service(id: $graphId) {
          id
          checkWorkflows(filter: $checkWorkflowsFilter) {
            id
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        checkWorkflowsFilter: {
          authors: selectedAuthors ?? [],
          subgraphs: selectedSubgraphs ?? [],
          branches: selectedBranches ?? [],
          variants: selectedVariants ?? [],
          status,
        },
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      pollInterval: 5000,
    },
  );

  const pageHasFilters =
    isMinLengthArray(1, selectedAuthors) ||
    isMinLengthArray(1, selectedBranches) ||
    isMinLengthArray(1, selectedSubgraphs) ||
    (!isVariantTab && isMinLengthArray(1, selectedVariants)) ||
    !!status;

  const shouldShowRecentChecksFilters =
    isMinLengthArray(1, data?.service?.checkWorkflows) || pageHasFilters;

  return (
    <PageLayout
      title={
        <PageTitle
          title="Checks"
          tabs={
            <>
              <HeaderTab
                isActive={() => view === 'variant'}
                to={recentChecksRouteConfig.path({
                  ...graphRef,
                  view: 'variant',
                })}
                tabID={`This Variant (${graphRef.graphVariant})`}
              />
              <HeaderTab
                isActive={() => view === 'graph'}
                tabID="This Graph"
                to={recentChecksRouteConfig.path({
                  ...graphRef,
                  view: 'graph',
                })}
              />
            </>
          }
          rightSide={
            shouldShowRecentChecksFilters && (
              <ChecksFilters className="my-2 grid grid-cols-3 items-center xl:my-0 xl:flex" />
            )
          }
        />
      }
    >
      <PermissionGuard
        graphId={graphRef.graphId}
        graphPermissions="canQueryPrivateInfo"
      >
        <CheckWorkflowsListPage />
      </PermissionGuard>
    </PageLayout>
  );
};
